import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Checkbox
} from '@mui/material';
import { 
  Add as AddIcon, 
  Remove as RemoveIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  AddCircleOutline as AddAllIcon
} from '@mui/icons-material';
import SyncWizardFooterWrapper from 'components/SyncWizardFooterWrapper';
import useAsync from 'hooks/useAsync';

const CrosstabsStep3and4 = ({
  projectId,
  selectedDataset,
  questionLabelsTable,
  questionIdField,
  questionTextField,
  questionTypeField,
  parentQuestionIdField,
  selectedQuestions,
  setSelectedQuestions,
  stepTitle = "Select column questions & order",
  stepDescription = "Choose the questions to appear as columns in your crosstab analysis.",
  nextButtonText = "Next",
  showErrors,
  onNext,
  onBack
}) => {
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [checkedChildren, setCheckedChildren] = useState({});
  
  // State for expanded question text
  const [expandedQuestionText, setExpandedQuestionText] = useState({});

  // Helper component for truncated text with expand functionality
  const TruncatedText = ({ text, questionId, maxLength = 75 }) => {
    const isExpanded = expandedQuestionText[questionId];
    const shouldTruncate = text && text.length > maxLength;
    const displayText = shouldTruncate && !isExpanded ? text.substring(0, maxLength) + '...' : text;

    const toggleExpanded = () => {
      setExpandedQuestionText(prev => ({
        ...prev,
        [questionId]: !prev[questionId]
      }));
    };

    return (
      <Box>
        <Typography variant="body2" component="span">
          {displayText}
        </Typography>
        {shouldTruncate && (
          <Button
            size="small"
            onClick={toggleExpanded}
            sx={{ 
              minWidth: 'auto', 
              p: 0.5, 
              ml: 0.5, 
              fontSize: '0.75rem',
              textTransform: 'none',
              color: 'primary.main'
            }}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </Button>
        )}
      </Box>
    );
  };

  // Helper function to create question type chip
  const QuestionTypeChip = ({ questionType }) => {
    const getTypeColor = (type) => {
      switch (type?.toLowerCase()) {
        case 'grid': return 'primary';
        case 'multiple': return 'secondary';
        case 'single': return 'default';
        case 'segment': return 'warning';
        case 'rank': return 'info';
        default: return 'default';
      }
    };

    return (
      <Chip
        label={questionType || 'unknown'}
        size="small"
        color={getTypeColor(questionType)}
        variant="outlined"
        sx={{ 
          fontSize: '0.65rem',
          height: '20px',
          '& .MuiChip-label': {
            px: 1
          },
          '&:hover': {
            backgroundColor: 'inherit',
            boxShadow: 'none'
          },
          '&:focus': {
            backgroundColor: 'inherit',
            boxShadow: 'none'
          },
          cursor: 'default'
        }}
      />
    );
  };

  // Fetch questions from the CNL table
  const fetchQuestions = useAsync(async () => {
    if (!questionLabelsTable || !questionIdField || !questionTextField || !questionTypeField) return [];
    
    const queryParams = new URLSearchParams();
    queryParams.set("project_id", projectId);
    queryParams.set("dataset_id", selectedDataset);
    queryParams.set("table_id", questionLabelsTable);
    queryParams.set("question_id_field", questionIdField);
    queryParams.set("question_text_field", questionTextField);
    queryParams.set("question_type_field", questionTypeField);
    queryParams.set("parent_question_id_field", parentQuestionIdField || "");
    
    try {
      const response = await fetch(`/crosstabs/get-questions/?${queryParams.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const questions = await response.json();
      setAvailableQuestions(questions);
      return questions;
    } catch (error) {
      throw error;
    }
  });

  // Load questions when required fields are available
  useEffect(() => {
    if (questionLabelsTable && questionIdField && questionTextField && questionTypeField && selectedDataset) {
      fetchQuestions.perform();
    }
  }, [questionLabelsTable, questionIdField, questionTextField, questionTypeField, selectedDataset]);

  // Convert selectedQuestions from strings to objects when available questions are loaded
  useEffect(() => {
    if (availableQuestions.length > 0 && selectedQuestions.length > 0) {
      // Check if selectedQuestions contains strings (from session storage) instead of objects
      const firstQuestion = selectedQuestions[0];
      if (typeof firstQuestion === 'string') {
        // Convert string question names to question objects
        const questionObjects = selectedQuestions
          .map(questionName => {
            // Find the question object in availableQuestions by q_id
            const foundQuestion = availableQuestions.find(q => q.q_id === questionName);
            if (foundQuestion) {
              return foundQuestion;
            } else {
              return null;
            }
          })
          .filter(Boolean); // Remove null values
        
        setSelectedQuestions(questionObjects);
        
        // For reused crosstabs, we need to reconstruct grid parent-child relationships
        // If we have child questions but not their parents, add the parents
        setTimeout(() => {
          const currentSelected = questionObjects;
          const questionsToAdd = [...currentSelected];
          
          currentSelected.forEach(question => {
            // If this is a grid parent question, add its children
            if (question.q_type === 'grid' && question.children && question.children.length > 0) {
              question.children.forEach(child => {
                if (!questionsToAdd.find(q => q.q_id === child.q_id)) {
                  questionsToAdd.push(child);
                }
              });
            }
          });
          
          if (questionsToAdd.length > currentSelected.length) {
            setSelectedQuestions(questionsToAdd);
          }
        }, 100);
      }
    }
  }, [availableQuestions, selectedQuestions, setSelectedQuestions]);

  // Filter available questions based on search term and exclude already selected ones
  const filteredAvailableQuestions = availableQuestions.filter(question => {
    const searchTermLower = (searchTerm || '').toLowerCase();
    
    // Check if the parent question matches the search
    const parentMatches = (question.q_text || '').toLowerCase().includes(searchTermLower) ||
                         (question.q_id || '').toLowerCase().includes(searchTermLower) ||
                         (question.q_type || '').toLowerCase().includes(searchTermLower);
    
    // For grid questions, also check if any children match the search
    let childrenMatch = false;
    if (question.children && question.children.length > 0) {
      childrenMatch = question.children.some(child => 
        (child.q_text || '').toLowerCase().includes(searchTermLower) ||
        (child.q_id || '').toLowerCase().includes(searchTermLower) ||
        (child.q_type || '').toLowerCase().includes(searchTermLower)
      );
    }
    
    const matchesSearch = parentMatches || childrenMatch;
    const notSelected = !selectedQuestions.find(selected => (selected.q_id || '') === (question.q_id || ''));
    
    // For grid questions, hide if all children are already selected
    if (question.q_type === 'grid' && question.children && question.children.length > 0) {
      const allChildrenSelected = question.children.every(child =>
        selectedQuestions.some(selected => selected.q_id === child.q_id)
      );
      if (allChildrenSelected) {
        return false;
      }
    }
    
    return matchesSearch && notSelected;
  }).map(question => {
    // For grid questions, filter out already selected children
    if (question.q_type === 'grid' && question.children && question.children.length > 0) {
      const availableChildren = question.children.filter(child =>
        !selectedQuestions.some(selected => selected.q_id === child.q_id)
      );
      return {
        ...question,
        children: availableChildren
      };
    }
    return question;
  });

  // Add a question to selected list
  const addQuestion = (question) => {
    setSelectedQuestions([...selectedQuestions, question]);
  };

  // Add grid parent and all its children
  const addGridQuestionWithChildren = (gridQuestion) => {
    const questionsToAdd = [gridQuestion];
    if (gridQuestion.children) {
      questionsToAdd.push(...gridQuestion.children);
    }
    setSelectedQuestions([...selectedQuestions, ...questionsToAdd]);
  };

  // Function to handle child checkbox changes
  const handleChildCheckbox = (parentId, childId, checked) => {
    setCheckedChildren(prev => ({
      ...prev,
      [parentId]: {
        ...prev[parentId],
        [childId]: checked
      }
    }));
  };

  // Function to add selected children for a grid question
  const addSelectedChildren = (gridQuestion) => {
    const parentCheckedChildren = checkedChildren[gridQuestion.q_id] || {};
    const childrenToAdd = gridQuestion.children.filter(child => parentCheckedChildren[child.q_id]);
    
    if (childrenToAdd.length === 0) return;
    
    const newSelectedQuestions = [...selectedQuestions];
    
    // Add the parent if it's not already selected
    if (!newSelectedQuestions.some(sq => sq.q_id === gridQuestion.q_id)) {
      newSelectedQuestions.push(gridQuestion);
    }
    
    // Add the selected children
    childrenToAdd.forEach(child => {
      if (!newSelectedQuestions.some(sq => sq.q_id === child.q_id)) {
        newSelectedQuestions.push(child);
      }
    });
    
    
    setSelectedQuestions(newSelectedQuestions);
    
    // Clear the checkboxes after adding
    setCheckedChildren(prev => ({
      ...prev,
      [gridQuestion.q_id]: {}
    }));
  };

  // Function to get count of checked children for a parent
  const getCheckedChildrenCount = (parentId) => {
    const parentCheckedChildren = checkedChildren[parentId] || {};
    return Object.values(parentCheckedChildren).filter(Boolean).length;
  };

  // Remove a question from selected list
  const removeQuestion = (questionId) => {
    setSelectedQuestions(selectedQuestions.filter(q => q.q_id !== questionId));
  };

  // Move question up in the order
  const moveQuestionUp = (index) => {
    if (index > 0) {
      const newQuestions = [...selectedQuestions];
      [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
      setSelectedQuestions(newQuestions);
    }
  };

  // Move question down in the order
  const moveQuestionDown = (index) => {
    if (index < selectedQuestions.length - 1) {
      const newQuestions = [...selectedQuestions];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      setSelectedQuestions(newQuestions);
    }
  };

  // Add all filtered questions
  const addAllQuestions = () => {
    const questionsToAdd = filteredAvailableQuestions.filter(question => 
      !selectedQuestions.find(selected => (selected?.q_id || '') === (question?.q_id || ''))
    );
    setSelectedQuestions([...selectedQuestions, ...questionsToAdd]);
  };

  // Remove all questions
  const removeAllQuestions = () => {
    setSelectedQuestions([]);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  // Track drag enter/leave count to handle child element events
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e, index) => {
    dragCounterRef.current++;
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    dragCounterRef.current--;
    // Only clear dragOverIndex when we've left all child elements
    if (dragCounterRef.current === 0) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    // Reset drag counter
    dragCounterRef.current = 0;
    
    if (draggedIndex === null) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const targetIndex = dropIndex;
    
    if (draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newQuestions = [...selectedQuestions];
    const draggedQuestion = newQuestions[draggedIndex];
    
    // Remove the dragged item
    newQuestions.splice(draggedIndex, 1);
    
    if (targetIndex === 0) {
      newQuestions.unshift(draggedQuestion);
    } else {
      let insertIndex = targetIndex;
      if (draggedIndex < targetIndex) {
        insertIndex = targetIndex - 1;
      }
      newQuestions.splice(insertIndex, 0, draggedQuestion);
    }
    
    setSelectedQuestions(newQuestions);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    // Reset drag counter
    dragCounterRef.current = 0;
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };



  const isLoading = fetchQuestions.isRunning;
  const hasErrors = fetchQuestions.didFail;

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', px: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {stepTitle}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
          {stepDescription}
        </Typography>

        {isLoading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Loading questions...
          </Alert>
        )}

        {hasErrors && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading questions. Please check your field mappings and try again.
          </Alert>
        )}

        {!isLoading && !hasErrors && (
          <>
            <Box sx={{ display: 'flex', gap: 1, minHeight: 600 }}>
              {/* Search Box - Same width as first column */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search questions, IDs, or types..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Available Questions Panel */}
                <Paper sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0,
                  height: 'calc(100% - 64px)'
                }}>
                  {/* Header */}
                  <Box sx={{ 
                    p: 2, 
                    borderBottom: '1px solid', 
                    borderColor: 'divider',
                    bgcolor: 'primary.main'
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Available Questions
                    </Typography>
                  </Box>

                  {/* Available Questions List */}
                  <Box sx={{ position: 'relative' }}>
                    <List sx={{ 
                      height: 500, 
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      p: 0,
                    }}>
                    {filteredAvailableQuestions.map((question, index) => {
                      // Check if this is a grid question with children
                      if (question.q_type === 'grid' && question.children && question.children.length > 0) {
                        return (
                          <Accordion 
                            key={question.q_id}
                            expanded={expandedAccordions[question.q_id] || false}
                            onChange={(event, isExpanded) => {
                              setExpandedAccordions(prev => ({
                                ...prev,
                                [question.q_id]: isExpanded
                              }));
                              
                              // When expanding, initialize children as checked if not already set
                              if (isExpanded && question.children && !checkedChildren[question.q_id]) {
                                const allChildrenChecked = {};
                                question.children.forEach(child => {
                                  allChildrenChecked[child.q_id] = true;
                                });
                                setCheckedChildren(prev => ({
                                  ...prev,
                                  [question.q_id]: allChildrenChecked
                                }));
                              }
                            }}
                            sx={{ 
                              bgcolor: index % 2 === 0 ? 'background.default' : 'secondary.light',
                              '&:before': { display: 'none' },
                              boxShadow: 'none',
                              borderBottom: index < filteredAvailableQuestions.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider',
                              '& .MuiAccordionSummary-root': {
                                bgcolor: 'inherit'
                              }
                            }}
                          >
                            <AccordionSummary 
                              sx={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 2,
                                pr: 1.5,
                                py: 2,
                                minHeight: 'auto',
                                '& .MuiAccordionSummary-content': { 
                                  alignItems: 'center',
                                  my: 0,
                                  flex: 1,
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                },
                                '& .MuiAccordionSummary-expandIconWrapper': {
                                  display: 'none'
                                }
                              }}
                              expandIcon={null}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <QuestionTypeChip questionType={question?.q_type} />
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        <span style={{ fontWeight: 'bold', color: '#008080' }}>ID: {question?.q_id}</span>
                                        <span style={{ color: 'text.secondary' }}> • {question.children.length} children</span>
                                      </Typography>
                                    </Box>
                                    <TruncatedText 
                                      text={question?.q_text || ''} 
                                      questionId={`accordion-${question?.q_id}`}
                                      maxLength={70}
                                    />
                                  </Box>
                                }
                                sx={{ pr: 2 }}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  sx={{ 
                                    transform: expandedAccordions[question.q_id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s'
                                  }}
                                >
                                  <ExpandMoreIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    
                                    // If accordion is closed, add parent + all children directly
                                    if (!expandedAccordions[question.q_id]) {
                                      addGridQuestionWithChildren(question);
                                    } else {
                                      // If accordion is open, use checkbox-based selection
                                      addSelectedChildren(question);
                                    }
                                  }}
                                  color="primary"
                                >
                                  <AddIcon />
                                </IconButton>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails 
                              sx={{ 
                                px: 2, 
                                py: 0, 
                                bgcolor: index % 2 === 0 ? 'background.default' : 'secondary.light',
                              }}
                            >
                                                              {question.children.map((child, childIndex) => (
                                  <ListItem
                                    key={child.q_id}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      px: 1,
                                      py: 1.5,
                                      pl: 0,
                                      borderTop:'1px solid',
                                      borderColor: '#c0c0c0',
                                      //bgcolor: childIndex % 2 === 0 ? 'rgba(255, 152, 0, 0.1)' : (index % 2 === 0 ? 'background.default' : 'secondary.light')
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={checkedChildren[question.q_id]?.[child.q_id] || false}
                                      onChange={(e) => handleChildCheckbox(question.q_id, child.q_id, e.target.checked)}
                                      color="primary"
                                    />
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                                          <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#008080', mb: 0.5 }}>
                                            ID: {child?.q_id}
                                          </Typography>
                                          <TruncatedText 
                                            text={child?.q_text || ''} 
                                            questionId={`child-${child?.q_id}`}
                                            maxLength={65}
                                          />
                                        </Box>
                                      }
                                      sx={{ pr: 2 }}
                                    />
                                    
                                  </ListItem>
                                ))}
                            </AccordionDetails>
                          </Accordion>
                        );
                      } else {
                        // Regular question (non-grid or grid without children)
                        return (
                          <ListItem
                            key={question?.q_id || Math.random()}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              px: 2,
                              pr: 1.5,
                              py: 2,
                              bgcolor: index % 2 === 0 ? 'background.default' : 'secondary.light',
                              borderBottom: index < filteredAvailableQuestions.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <QuestionTypeChip questionType={question?.q_type} />
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#008080' }}>
                                      ID: {question?.q_id}
                                    </Typography>
                                  </Box>
                                  <TruncatedText 
                                    text={question?.q_text || ''} 
                                    questionId={`regular-${question?.q_id}`}
                                    maxLength={80}
                                  />
                                </Box>
                              }
                              sx={{ pr: 2 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                // For grid questions, add parent + all children
                                if (question.q_type === 'grid' && question.children && question.children.length > 0) {
                                  addGridQuestionWithChildren(question);
                                } else {
                                  addQuestion(question);
                                }
                              }}
                              color="primary"
                            >
                              <AddIcon />
                            </IconButton>
                          </ListItem>
                        );
                      }
                    })}
                    {filteredAvailableQuestions.length === 0 && (
                      <ListItem sx={{ px: 3, py: 4 }}>
                        <ListItemText
                          primary={searchTerm ? "No questions match your search" : "No questions available"}
                          sx={{ textAlign: 'center', color: 'text.secondary' }}
                        />
                      </ListItem>
                    )}
                    </List>
                  </Box>
                </Paper>
              </Box>

              {/* Center Controls */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 120,
                gap: 2,
                py: 4,
                px: 1
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addAllQuestions}
                  disabled={filteredAvailableQuestions.length === 0}
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  Add All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={removeAllQuestions}
                  disabled={selectedQuestions.length === 0}
                  startIcon={<RemoveIcon />}
                  fullWidth
                >
                  Remove All
                </Button>
              </Box>

              {/* Selected Questions Panel */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ height: 40, mb: 3 }} />
                
                <Paper sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0,
                  height: 'calc(100% - 64px)'
                }}>
                {/* Header */}
                <Box sx={{ 
                  p: 2, 
                  borderBottom: '1px solid', 
                  borderColor: 'divider',
                  bgcolor: 'primary.main'
                }}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Selected Questions: {selectedQuestions.length}
                  </Typography>
                </Box>

                {/* Selected Questions List */}
                <Box sx={{ position: 'relative' }}>
                  <List 
                    data-list="selected"
                    sx={{ 
                      height: 500, 
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      p: 0,
                    }}>

                  
                  {selectedQuestions.map((question, index) => (
                    <ListItem
                      key={`selected-${question.q_id}-${index}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnter={(e) => handleDragEnter(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 2,
                          bgcolor: index % 2 === 0 ? 'background.default' : 'secondary.light',
                          borderBottom: index < selectedQuestions.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          cursor: 'move',
                          opacity: draggedIndex === index ? 0.5 : 1,
                          borderTop: dragOverIndex === index && draggedIndex !== null && draggedIndex !== index ? '40px solid white' : '0px solid transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            filter: 'brightness(0.95)',
                          }
                        }}
                      >
                      {/* Drag Handle */}
                      <Box sx={{ minWidth: 20, display: 'flex', alignItems: 'center' }}>
                        <DragIndicatorIcon 
                          sx={{ 
                            color: 'action.active',
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            fontSize: '1.75rem'
                          }} 
                        />
                      </Box>
                      
                      <Box sx={{ minWidth: 32 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main'
                          }}
                        >
                          {index + 1}.
                        </Typography>
                      </Box>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <QuestionTypeChip questionType={question?.q_type} />
                              <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#008080' }}>
                                ID: {question?.q_id}
                              </Typography>
                            </Box>
                            <TruncatedText 
                              text={question?.q_text || ''} 
                              questionId={`selected-${question?.q_id}`}
                              maxLength={55}
                            />
                          </Box>
                        }
                        sx={{ flex: 1 }}
                      />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveQuestionUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveQuestionDown(index)}
                          disabled={index === selectedQuestions.length - 1}
                        >
                          <ArrowDownIcon />
                        </IconButton>
                      </Box>
                      
                      <IconButton
                        size="small"
                        onClick={() => removeQuestion(question.q_id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                  
                  {/* Special drop zone for end of list */}
                  {selectedQuestions.length > 0 && (
                    <Box
                      onDragOver={(e) => handleDragOver(e, selectedQuestions.length)}
                      onDrop={(e) => handleDrop(e, selectedQuestions.length)}
                      sx={{
                        height: dragOverIndex === selectedQuestions.length && draggedIndex !== null ? '60px' : '30px',
                        transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        mx: 2,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '30px'
                      }}
                    >

                    </Box>
                  )}
                  
                  {selectedQuestions.length === 0 && (
                    <ListItem sx={{ px: 3, py: 4 }}>
                      <ListItemText
                        primary="No questions selected"
                        secondary="Add questions from the left panel"
                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                
                  <Box
                    onDragEnter={(e) => handleDragEnter(e, selectedQuestions.length)}
                    onDragOver={(e) => handleDragOver(e, selectedQuestions.length)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, selectedQuestions.length)}
                    sx={{
                      height: '30px',
                      width: '100%',
                      mx: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'transparent'
                    }}
                  />
                </List>

                </Box>
                </Paper>
              </Box>
            </Box>
          </>
        )}

        {showErrors && selectedQuestions.length === 0 && (
          <Alert severity="error" sx={{ mt: 3 }}>
            Please select at least one question to continue.
          </Alert>
        )}
      </Box>

      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "space-between", mt: 3, pt: 1 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          sx={{ px: 4, py: 1.5 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={selectedQuestions.length === 0}
          size="large"
          sx={{ 
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            backgroundColor: 'grey.600',
            '&:hover': {
              backgroundColor: 'grey.700'
            }
          }}
        >
          {nextButtonText}
        </Button>
      </SyncWizardFooterWrapper>
    </Box>
  );
};

export default CrosstabsStep3and4;